import { bunja } from 'bunja';
import { differenceInDays } from 'date-fns';
import { atom } from 'jotai';
import { ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { RouterScope } from '@flipster/react/providers/route';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { convertDate } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { authBunja } from '../../shared/auth/state';
import { OrderAttributionMapper, orderAttributionAtom } from '../../shared/order-attribution';
import {
  formatRank,
  intervalLeaderboardBunja,
  intervalLeaderboardMyRankingBunja,
  tradingLeaderboardBunja,
  tradingLeaderboardMyRankingBunja,
} from '../bunja';
import { intervalInfos, tierInfos } from '../shared/constants';
import { LeaderboardTierIcon } from '../shared/tier-icon';

interface MyInfoRightSection {
  description: React.ReactNode;
  primaryButton?: {
    label: ReactNode;
    onClick: () => void;
  };
  shareButton?: boolean;
}

export const myInfoBunja = bunja(() => {
  const router = bunja.use(RouterScope);
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { openSettings } = bunja.use(tradingLeaderboardBunja);
  const { nicknameAtom, optOutAtom } = bunja.use(tradingLeaderboardMyRankingBunja);
  const { interval, tier, competitionEndAtAtom } = bunja.use(intervalLeaderboardBunja);
  const { currentCursorRankingAtom, intervalRankingAtom } = bunja.use(intervalLeaderboardMyRankingBunja);
  const formattedRankAtom = atom((get) => {
    const currentCursorRanking = get(currentCursorRankingAtom);
    if (!currentCursorRanking) return '';
    if (currentCursorRanking.rank == null) return 'Unranked';
    return formatRank(currentCursorRanking.rank, currentCursorRanking.lastRank);
  });
  const isOutOfRankingAtom = atom((get) => {
    const { rank = 0, lastRank } = get(currentCursorRankingAtom) ?? {};
    return !lastRank || !rank || rank > lastRank;
  });
  const rightSectionAtom = atom((get): MyInfoRightSection => {
    const status = get(currentCursorRankingAtom)?.rankingStatus;
    const rank = get(currentCursorRankingAtom)?.rank ?? 0;
    const weekOrMonth = intervalInfos[interval].label;
    if (status === 'OPT_OUT') {
      return {
        description: <FormattedMessage id='Autogen_common.r251126.s_Rejoin_now_to_see_your_ranking' />,
        primaryButton: {
          label: <FormattedMessage id='Autogen_common.r251126.s_Opt_back_in' />,
          onClick: () => openSettings({ from_route: 'profile_section' }),
        },
      };
    }
    if (status === 'OPT_OUT_PENDING') {
      return {
        description: <FormattedMessage id='Autogen_common.r251126.s_Rank_will_be_hidden_in_the_next_update' />,
      };
    }
    if (status === 'OPT_IN_PENDING') {
      return {
        description: <FormattedMessage id='Autogen_common.r251126.s_Rank_will_be_shown_in_the_next_update' />,
      };
    }
    if (status === 'UNRANKED') {
      return {
        description: (
          <FormattedMessage
            id='Autogen_common.r251126.s_Make_a_trade_this_intervalInfos_interval_label_to_rank'
            values={{ intervalInfos_interval_label: weekOrMonth }}
          />
        ),
        primaryButton: {
          label: <FormattedMessage id='Common.r251104.Trade_now' />,
          onClick: () => {
            store.set(orderAttributionAtom, OrderAttributionMapper.leaderboard.weeklyPnlTrade);
            segment.track(TradingLeaderboardEvent.LeaderboardTradeNowClick, { ranking: rank, interval, tier });
            router.push('/trade');
          },
        },
      };
    }
    const myTier = get(intervalRankingAtom)?.tier;
    if (!status && myTier) {
      return {
        description: (
          <div className={hstack({ gap: 4 })}>
            <p>
              <FormattedMessage
                id='Autogen_common.r251126.s_Your_league_this_intervalInfos_interval_label'
                values={{
                  intervalInfos_interval_label: intervalInfos[interval].label,
                }}
              />
            </p>
            <div className={hstack({ gap: 4 })}>
              <LeaderboardTierIcon tier={myTier} size={20} />
              <p className={css({ tabletDown: { display: 'none' } })}>{tierInfos[myTier].label}</p>
            </div>
          </div>
        ),
        primaryButton: {
          label: 'Go to league',
          onClick: () => {
            segment.track(TradingLeaderboardEvent.LeaderboardGotoLeagueClick, {
              from_league: tier,
              to_league: myTier,
            });
            router.push(`?interval=${interval}&tier=${myTier}`);
          },
        },
      };
    }
    // status === 'OPT_IN' and fallback case
    const competitionEndAt = get(competitionEndAtAtom);
    return {
      description: (() => {
        const leftDays = differenceInDays(convertDate(competitionEndAt) ?? Date.now(), Date.now());
        if (leftDays < 1) {
          return <FormattedMessage id='Autogen_common.r251126.s_Last_Day_Format' values={{ word: weekOrMonth }} />;
        }
        if (leftDays === 1) {
          return <FormattedMessage id='Autogen_common.r251126.s_One_Day_Left_Format' values={{ word: weekOrMonth }} />;
        }

        return (
          <FormattedMessage
            id='Autogen_common.r251126.s_Days_Left_Format'
            values={{ word0: leftDays, word1: weekOrMonth }}
          />
        );
      })(),
      primaryButton: {
        label: <FormattedMessage id='Common.r251104.Trade_now' />,
        onClick: () => {
          store.set(orderAttributionAtom, OrderAttributionMapper.leaderboard.weeklyPnlTrade);
          segment.track(TradingLeaderboardEvent.LeaderboardTradeNowClick, { ranking: rank, interval, tier });
          router.push('/trade');
        },
      },
      shareButton: true,
    };
  });
  return {
    tier,
    interval,
    isLoggedInAtom,
    nicknameAtom,
    optOutAtom,
    rightSectionAtom,
    formattedRankAtom,
    isOutOfRankingAtom,
    currentCursorRankingAtom,
  };
});
myInfoBunja.debugLabel = 'myInfoBunja';
